import React, { useEffect, useId, useRef, useState } from 'react';
import Switch from '../ui/Switch';
import Checkbox from '../ui/Checkbox';
import Button3D from '../ui/Button3D';
import HelpButton from '../ui/HelpButton';
import { useSaveToast } from '../ui/SaveToast';
import {
    STANDARD_TONES, URGENT_TONES, TONE_OFF, SOUND_DEFAULTS, SOUND_SETTINGS_CHANGED_EVENT, normalizeSoundSettings, playTone,
} from '../../lib/alertSounds';
import { fetchMySoundSettings, saveSoundSettings } from '../../lib/soundSettingsApi';

// BL-177 Step 2e (item 9): Settings > My notifications > Alert sounds.
//   In-app sounds (pop-up + bell): a standard tone for ordinary alerts, a stronger urgent tone for critical and red
//   "Act now" alerts, a volume slider, and a Play button on each. Personal quiet hours mute sounds except critical alerts.
//   Push banners (computer + phone) always use the system sound; the only choice is "Silent".
// Every change saves with no reload (green "Saved ✓" or a red toast and the control reverts).

export const ALERT_SOUNDS_HELP = {
    what: 'The sound IntelAgent makes when an alert pops up while you are working in it.',
    who: 'Only you. It changes nothing for anyone else.',
    when: 'Urgent tone for critical and red "Act now" alerts, standard tone for everything else. No sound while you are reading that conversation. Quiet hours mute sounds, except critical alerts.',
};

function ToneSelect({ id, label, value, tones, onChange, onPlay, disabled }) {
    return (
        <div className="flex flex-wrap items-center gap-3">
            <label htmlFor={id} className="w-36 text-sm font-semibold">{label}</label>
            <select
                id={id}
                value={value}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
                className="min-w-[10rem] rounded-[10px] border border-[var(--ia-line)] bg-[#0A101C] px-3 py-2 font-semibold text-white shadow-[inset_0_2px_3px_rgba(0,0,0,.6)]"
            >
                {tones.map((tone) => <option key={tone.id} value={tone.id}>{tone.label}</option>)}
                <option value={TONE_OFF}>Off</option>
            </select>
            <Button3D variant="pill" disabled={disabled || value === TONE_OFF} onClick={onPlay} aria-label={`Play ${label.toLowerCase()}`}>Play</Button3D>
        </div>
    );
}

export default function AlertSoundsCard({ client, userId, orgId }) {
    const toast = useSaveToast();
    const ids = useId();
    const [settings, setSettings] = useState(normalizeSoundSettings(null));
    const saved = useRef(settings);
    const seq = useRef(0);
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState('');

    useEffect(() => {
        let active = true;
        if (!client || !userId || !orgId) return () => { active = false; };
        fetchMySoundSettings(client).then(({ settings: mine, loaded: ok }) => {
            if (!active) return;
            saved.current = mine;
            setSettings(mine);
            setLoaded(true);
            if (!ok) setLoadError('Your sound settings could not be loaded, so the standard ones are shown.');
        });
        return () => { active = false; };
    }, [client, userId, orgId]);

    async function change(patch) {
        const next = { ...settings, ...patch };
        const mine = ++seq.current;
        setSettings(next);
        const failure = await saveSoundSettings(client, { userId, orgId, settings: next });
        if (mine !== seq.current) return;
        if (failure) {
            setSettings(saved.current);
            toast.failed(failure);
        } else {
            saved.current = next;
            toast.saved();
            if (typeof window !== 'undefined') window.dispatchEvent(new Event(SOUND_SETTINGS_CHANGED_EVENT));
        }
    }

    // The slider saves once when it is let go (mouse, touch, arrow keys or leaving it), not on every step of the drag.
    const volumeRequested = useRef(null);
    function commitVolume() {
        const wanted = settings.volume;
        if (wanted === saved.current.volume || wanted === volumeRequested.current) return;
        volumeRequested.current = wanted;
        change({ volume: wanted }).finally(() => { volumeRequested.current = null; });
    }

    const off = !settings.soundEnabled;
    return (
        <section className="ia-d2-card flex flex-col gap-4" aria-labelledby={`${ids}-title`} data-testid="alert-sounds">
            <h3 id={`${ids}-title`} className="inline-flex items-center gap-2 text-base font-bold">
                Alert sounds
                <HelpButton title="Alert sounds" what={ALERT_SOUNDS_HELP.what} who={ALERT_SOUNDS_HELP.who} when={ALERT_SOUNDS_HELP.when} />
            </h3>

            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                    <Switch label="Play alert sounds" checked={settings.soundEnabled} disabled={!loaded} onChange={(next) => change({ soundEnabled: next })} />
                    <span>Play a sound for pop-ups and the bell while I am in IntelAgent.</span>
                </div>
                <ToneSelect
                    id={`${ids}-standard`}
                    label="Standard tone"
                    value={settings.standardTone}
                    tones={STANDARD_TONES}
                    disabled={!loaded || off}
                    onChange={(value) => change({ standardTone: value })}
                    onPlay={() => playTone(settings.standardTone, settings.volume)}
                />
                <ToneSelect
                    id={`${ids}-urgent`}
                    label="Urgent tone"
                    value={settings.urgentTone}
                    tones={URGENT_TONES}
                    disabled={!loaded || off}
                    onChange={(value) => change({ urgentTone: value })}
                    onPlay={() => playTone(settings.urgentTone, settings.volume)}
                />
                <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor={`${ids}-volume`} className="w-36 text-sm font-semibold">Volume</label>
                    <input
                        id={`${ids}-volume`}
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={settings.volume}
                        disabled={!loaded || off}
                        aria-valuetext={`${settings.volume} percent`}
                        className="w-48 accent-[#14B8A6]"
                        onChange={(event) => setSettings((current) => ({ ...current, volume: Number(event.target.value) }))}
                        onPointerUp={commitVolume}
                        onKeyUp={commitVolume}
                        onBlur={commitVolume}
                    />
                    <span data-testid="sound-volume" className="w-12 text-sm font-semibold">{settings.volume}%</span>
                </div>
                <p className="text-sm">The urgent tone plays for critical and red “Act now” alerts. No sound while you are reading that conversation. Quiet hours mute sounds except critical alerts.</p>
            </div>

            <div className="flex flex-col gap-2 border-t border-[var(--ia-line)] pt-3">
                <h4 className="text-sm font-bold">Push banners (computer and phone)</h4>
                <Checkbox checked={settings.pushSilent} disabled={!loaded} onChange={(next) => change({ pushSilent: next })} describedBy={`${ids}-silent-help`}>
                    Silent
                </Checkbox>
                <p id={`${ids}-silent-help`} className="text-sm">
                    Banners use your computer or phone’s standard sound. Tick Silent to get them with no sound. Browsers do not allow custom banner tones.
                </p>
            </div>
            {loadError && <p role="alert" className="text-sm text-red-300">{loadError}</p>}
        </section>
    );
}

export { SOUND_DEFAULTS };
